import domtoimage from "dom-to-image-more";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Helper: load an image as a promise
 */
const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

/**
 * Helper: Parse date_hr for report display
 */
const parseDateHr = (dateHr) => {
    if (!dateHr) return new Date();
    if (typeof dateHr !== 'string') return new Date(dateHr);
    
    if (dateHr.includes('_')) {
        const [datePart, hrPart] = dateHr.split('_');
        const normalizedDate = datePart.replace(/-/g, '');
        const year = parseInt(normalizedDate.slice(0, 4));
        const month = parseInt(normalizedDate.slice(4, 6)) - 1;
        const day = parseInt(normalizedDate.slice(6, 8));
        const hour = hrPart ? parseInt(hrPart.slice(0, 2)) : 0;
        return new Date(year, month, day, hour);
    }
    return new Date(dateHr);
};

const getValidityRange = (targetDateStr) => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
  
    if (targetDateStr === todayStr) {
        const curHour = String(now.getUTCHours()).padStart(2, '0');
        return `[UTC00-${curHour}]`;
    }
    return `[UTC00-24]`;
};

const formatTimestamp = (dateHr) => {
    if (!dateHr) return "N/A";
    let isoDate = "";
    let hourStr = "00";
    
    if (typeof dateHr === 'string' && dateHr.includes('_')) {
        const parts = dateHr.split('_');
        isoDate = parts[0];
        hourStr = (parts[1] && parts[1].length >= 2) ? parts[1].slice(0, 2) : "00";
        if (isoDate.length === 8 && !isoDate.includes('-')) { // YYYYMMDD
            isoDate = `${isoDate.slice(0, 4)}-${isoDate.slice(4, 6)}-${isoDate.slice(6, 8)}`;
        }
    } else if (typeof dateHr === 'string' && dateHr.length === 8) { // YYYYMMDD
        isoDate = `${dateHr.slice(0, 4)}-${dateHr.slice(4, 6)}-${dateHr.slice(6, 8)}`;
    } else {
        const d = parseDateHr(dateHr);
        if (!isNaN(d.getTime())) {
            isoDate = d.toISOString().split('T')[0];
            hourStr = String(d.getUTCHours()).padStart(2, '0');
        } else {
            isoDate = "N/A";
        }
    }
    
    if (isoDate === "N/A") return "N/A";
    const rangeStr = getValidityRange(isoDate);
    return `${isoDate} ${hourStr}:00${rangeStr}`;
};

export const captureAndGeneratePdf = async ({
    moduleName = "Forecast",
    language,
    mapRef,
    setIsGeneratingReport,
    currentTimestamp,
    impactedTableData,
    translations,
    riskTranslations,
    characteristicColors,
}) => {
    if (!mapRef.current) {
        console.warn("Cannot generate report: required DOM refs missing.");
        alert("PDF generation failed due to missing components. Please refresh and try again.");
        return;
    }

    setIsGeneratingReport(true);

    try {
        const pdf = new jsPDF("portrait", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const sideMargin = 10;
        const topMargin = 10;
        const footerHeight = 10;
        const marginBottom = 10;
        const bannerHeight = 20;
        const headerHeight = topMargin + bannerHeight + 5;
        const availablePageHeight = pageHeight - headerHeight - footerHeight - marginBottom;
        let currentY;

        const t = translations[language];
        const riskT = riskTranslations[language];

        // Timestamp for the report
        const timestampLabel = currentTimestamp ? formatTimestamp(currentTimestamp) : "N/A";

        // Calculate risk counts from impacted table data
        const riskCounts = (impactedTableData || []).reduce((acc, item) => {
            const advisory = item.advisory ? item.advisory.trim().toLowerCase() : 'n/a';
            let riskKey = 'N/A';
            if (advisory.includes('high')) riskKey = 'High';
            else if (advisory.includes('medium')) riskKey = 'Medium';
            else if (advisory.includes('low')) riskKey = 'Low';
            else if (advisory.includes('norisk') || advisory.includes('no risk')) riskKey = 'NoRisk';
            acc[riskKey] = (acc[riskKey] || 0) + 1;
            return acc;
        }, { 'High': 0, 'Medium': 0, 'Low': 0, 'NoRisk': 0, 'N/A': 0 });

        const riskKeys = ['High', 'Medium', 'Low', 'NoRisk', 'N/A'];

        // Transposed summary table
        const summaryTableHead = [
            { content: "Risk Level", styles: { halign: "center", valign: "middle", fillColor: [225, 232, 242], textColor: [0, 0, 0] } }
        ];
        const summaryTableBody = [
            { content: "Post Admin Count", styles: { halign: "center", valign: "middle", fillColor: [225, 232, 242], textColor: [0, 0, 0], fontStyle: "bold" } }
        ];

        riskKeys.forEach(riskKey => {
            const translatedRisk = riskT[riskKey] || riskKey;
            const colorRgb = characteristicColors[riskKey] || characteristicColors['N/A'];
            const count = riskCounts[riskKey] || 0;

            summaryTableHead.push({
                content: translatedRisk,
                styles: {
                    fillColor: colorRgb,
                    textColor: [0, 0, 0],
                    halign: "center",
                    valign: "middle",
                    fontStyle: "bold"
                },
            });
            summaryTableBody.push({
                content: count.toString(),
                styles: { halign: "center", valign: "middle" },
            });
        });

        // Load banner image
        let bannerImage;
        const bannerPath = language === "en" ? "/pdf/banner.png" : "/pdf/tetumbanner.png";
        try {
            bannerImage = await loadImage(bannerPath);
        } catch (error) {
            console.warn(`Failed to load ${bannerPath}:`, error);
            bannerImage = null;
        }

        // Banner drawer
        const addBannerToPage = (pdf) => {
            const bWidth = pageWidth - 2 * sideMargin;
            if (bannerImage) {
                pdf.addImage(bannerImage, "PNG", sideMargin, topMargin, bWidth, bannerHeight);
            }
            return topMargin + bannerHeight + 5;
        };

        const addPageWithHeader = () => {
            pdf.addPage();
            currentY = addBannerToPage(pdf);
            return currentY + 5;
        };

        // ===== PAGE 1 =====
        currentY = addBannerToPage(pdf);
        currentY += 5;

        // Title
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text(t.warningTitle, pageWidth / 2, currentY, { align: "center" });
        pdf.setFont("helvetica", "normal");
        currentY += 10;

        // Issued metadata
        const now = new Date();
        const formattedDate = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Asia/Dili",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        }).format(now);

        const [datePart, timePartRaw] = formattedDate.split(", ");
        const [dayStr, monthStr, yearStr] = datePart.split("/");
        const timeMatch = timePartRaw.match(/(\d{2}):(\d{2}):(\d{2})\s*(AM|PM)?/i);
        const hour = timeMatch ? timeMatch[1] : "00";
        const minute = timeMatch ? timeMatch[2] : "00";
        const second = timeMatch ? timeMatch[3] : "00";
        const period = timeMatch && timePartRaw ? timePartRaw.slice(-2) : "";
        const formattedTimorDate = `${yearStr}-${monthStr}-${dayStr} ${hour}:${minute}:${second} ${period} East Timor Time (TLT)`;

        pdf.setFontSize(10);

        // Left: Forecast Time
        pdf.setFont("helvetica", "bold");
        pdf.text("Forecast Time: ", sideMargin, currentY + 6);
        const obsWidth = pdf.getTextWidth("Forecast Time: ");
        pdf.setFont("helvetica", "normal");
        pdf.text(timestampLabel, sideMargin + obsWidth, currentY + 6);

        // Right: Issued At
        pdf.setFont("helvetica", "bold");
        const issuedAtLabel = t.issuedAt;
        const rightText = `${issuedAtLabel}${formattedTimorDate}`;
        const rightTextWidth = pdf.getTextWidth(rightText);
        const rightX = pageWidth - sideMargin - rightTextWidth;
        pdf.text(issuedAtLabel, rightX, currentY);
        pdf.setFont("helvetica", "normal");
        pdf.text(formattedTimorDate, rightX + pdf.getTextWidth(issuedAtLabel) + 2, currentY);
        currentY += 12;

        // Issued By
        pdf.setFont("helvetica", "bold");
        const issuedByLabel = t.issuedBy + " ";
        pdf.text(issuedByLabel, sideMargin, currentY);
        pdf.setFont("helvetica", "normal");
        pdf.text("National Directorate of Meteorology and Geophysics", sideMargin + pdf.getTextWidth(issuedByLabel) + 2, currentY);
        currentY += 6;
        currentY += 5;

        // ===== OVERVIEW SECTION =====
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setFillColor(202, 217, 239);
        pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
        pdf.setTextColor(0, 0, 0);
        pdf.text(t.overview, sideMargin + 2, currentY - 2, { align: "left" });
        currentY += 8; // Increased gap after "Overview:" label

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");

        // Print overview warning in paragraphs
        const p1 = t.highRiskWarningPart1;
        const p2 = t.highRiskWarningPart2;

        [p1, p2].forEach(p => {
            const lines = pdf.splitTextToSize(p, pageWidth - 2 * sideMargin);
            lines.forEach(line => {
                pdf.text(line, sideMargin, currentY);
                currentY += 5;
            });
            currentY += 3; // Gap between paragraphs
        });
        currentY += 2;

        // ===== RISK SUMMARY TABLE =====
        if (currentY + 25 > availablePageHeight + headerHeight) {
            currentY = addPageWithHeader();
        }

        const totalTableWidth = pageWidth - 2 * sideMargin;
        const columnCount = riskKeys.length + 1;
        const firstColWidth = 40;
        const otherColWidth = (totalTableWidth - firstColWidth) / (columnCount - 1);
        const colStyles = {
            0: { cellWidth: firstColWidth, halign: "left", fontStyle: "bold" },
        };
        for (let i = 1; i < columnCount; i++) {
            colStyles[i] = { cellWidth: otherColWidth, halign: "center" };
        }

        autoTable(pdf, {
            startY: currentY,
            head: [summaryTableHead],
            body: [summaryTableBody],
            theme: "grid",
            headStyles: {
                fillColor: [225, 232, 242],
                halign: "center",
                fontStyle: "bold",
                textColor: "black",
                fontSize: 9,
                cellPadding: 2,
                lineColor: [150, 150, 150],
                lineWidth: 0.1
            },
            styles: {
                fontSize: 9,
                cellPadding: 2,
                overflow: "linebreak",
                lineWidth: 0.1,
                lineColor: [150, 150, 150],
            },
            columnStyles: colStyles,
            margin: { left: sideMargin, right: sideMargin, top: headerHeight },
            didDrawPage: (data) => {
                addBannerToPage(pdf);
            },
        });
        currentY = pdf.lastAutoTable.finalY + 5;

        // ===== MAP IMAGE =====
        let mapImgHeight = 0;
        const mapContainer = mapRef.current._container;

        const domFilter = (node) => {
            if (node.classList) {
                const excluded = [
                    "leaflet-control-zoom",
                    "leaflet-control-layers",
                    "leaflet-control-attribution",
                    "legend-home",
                    "info-control-lightning",
                    "show-tooltips-control",
                    "leaflet-tooltip",
                    "leaflet-popup",
                ];
                return !excluded.some((cls) => node.classList.contains(cls));
            }
            return true;
        };

        const tempMapBlob = await domtoimage.toBlob(mapContainer, {
            width: 1330,
            height: mapContainer.offsetHeight,
            style: { background: "white" },
            quality: 0.95,
            filter: domFilter,
        });
        const tempMapUrl = URL.createObjectURL(tempMapBlob);
        const tempMapImg = await loadImage(tempMapUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        mapImgHeight = (tempMapImg.height * availableWidth) / tempMapImg.width;
        URL.revokeObjectURL(tempMapUrl);

        if (currentY + 10 + mapImgHeight + 5 > availablePageHeight + headerHeight) {
            currentY = addPageWithHeader();
        }

        const mapBlob = await domtoimage.toBlob(mapContainer, {
            width: 1330,
            height: mapContainer.offsetHeight,
            style: { background: "white" },
            quality: 0.95,
            filter: domFilter,
        });
        const mapUrl = URL.createObjectURL(mapBlob);
        pdf.addImage(mapUrl, "PNG", sideMargin, currentY, availableWidth, mapImgHeight);
        currentY += mapImgHeight + 5;
        URL.revokeObjectURL(mapUrl);

        currentY += 5;

        // ===== IMPACT & RISK INFORMATION TABLE =====
        const isNowcast = moduleName === "Nowcast";

        const tableDataForPdf = (impactedTableData || []).map((item, index) => {
            const riskLvlStr = item.advisory ? item.advisory.trim() : "N/A";
            let riskKey = 'N/A';
            if (riskLvlStr.toLowerCase().includes('high')) riskKey = 'High';
            else if (riskLvlStr.toLowerCase().includes('medium')) riskKey = 'Medium';
            else if (riskLvlStr.toLowerCase().includes('low')) riskKey = 'Low';
            else if (riskLvlStr.toLowerCase().includes('norisk') || riskLvlStr.toLowerCase().includes('no risk')) riskKey = 'NoRisk';

            const row = [
                index + 1,
                item.Municipality || "N/A",
                item.PostAdmin || "N/A",
            ];

            if (isNowcast) {
                row.push(item.Suco || "N/A");
            }

            row.push(item.date_hr || "N/A");

            if (isNowcast) {
                row.push(item.threatType || "N/A");
            }

            row.push({
                content: riskT[riskKey] || riskKey,
                styles: { fillColor: characteristicColors[riskKey] || characteristicColors['N/A'] },
            });

            row.push(
                item.Population?.toString() || '0',
                item.Male?.toString() || '0',
                item.Female?.toString() || '0',
                item.Children?.toString() || '0',
                item.Elderly?.toString() || '0',
                item.Household?.toString() || '0',
            );

            return row;
        });

        const lightningTableHead = isNowcast ? [
            [
                { content: "Sl No", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Municipality", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Post Admin", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Suco", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Forecast Data", colSpan: 3, styles: { halign: "center" } },
                { content: "Socio-Economic Data", colSpan: 6, styles: { halign: "center" } },
            ],
            [
                { content: "Date/Time", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Threat Type", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Advisory", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Population", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Male", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Female", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Children", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Elderly", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Household", styles: { halign: "center", fontStyle: "bold" } },
            ]
        ] : [
            [
                { content: "Sl No", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Municipality", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Post Admin", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                { content: "Forecast Data", colSpan: 2, styles: { halign: "center" } },
                { content: "Socio-Economic Data", colSpan: 6, styles: { halign: "center" } },
            ],
            [
                { content: "Date/Time", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Advisory", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Population", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Male", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Female", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Children", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Total Elderly", styles: { halign: "center", fontStyle: "bold" } },
                { content: "Household", styles: { halign: "center", fontStyle: "bold" } },
            ]
        ];

        const estimatedTableHeight = tableDataForPdf.length * 8 + 15;
        if (currentY + 10 + estimatedTableHeight + 10 > availablePageHeight + headerHeight) {
            currentY = addPageWithHeader();
        }

        // Section header
        currentY += 10;
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setFillColor(202, 217, 239);
        pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
        pdf.setTextColor(0, 0, 0);
        pdf.text(t.impact, sideMargin + 2, currentY - 2, { align: "left" });
        pdf.setTextColor(0, 0, 0);
        currentY += 5;

        // Draw Risk Legend
        const legendKeys = ['High', 'Medium', 'Low', 'NoRisk', 'N/A'];
        const legendData = [
            legendKeys.map(key => ({
                content: riskT[key] || key,
                styles: {
                    fillColor: characteristicColors[key] || characteristicColors['N/A'],
                    halign: 'center',
                    fontStyle: 'bold',
                    textColor: 'black',
                    fontSize: 8,
                    cellPadding: 2,
                }
            }))
        ];

        autoTable(pdf, {
            startY: currentY,
            body: legendData,
            theme: 'grid',
            styles: {
                fontSize: 8,
                minCellHeight: 8,
                lineWidth: 0.1,
                lineColor: [150, 150, 150],
            },
            margin: { left: sideMargin, right: sideMargin, top: headerHeight },
            didDrawPage: (data) => {
                addBannerToPage(pdf);
            },
        });
        currentY = pdf.lastAutoTable.finalY + 5;

        const columnStylesTable = isNowcast ? {
            0: { cellWidth: 8, halign: "center", valign: "middle" },    // Sl No
            1: { cellWidth: 20, halign: "left", valign: "middle" },     // Municipality
            2: { cellWidth: 20, halign: "left", valign: "middle" },     // Post Admin
            3: { cellWidth: 20, halign: "left", valign: "middle" },     // Suco
            4: { cellWidth: 22, halign: "center", valign: "middle" },   // Date/Time
            5: { cellWidth: 18, halign: "center", valign: "middle" },   // Threat Type
            6: { cellWidth: 15, halign: "center", valign: "middle" },   // Advisory
            7: { cellWidth: 12, halign: "right", valign: "middle" },    // Total Population
            8: { cellWidth: 11, halign: "right", valign: "middle" },    // Total Male
            9: { cellWidth: 11, halign: "right", valign: "middle" },    // Total Female
            10: { cellWidth: 11, halign: "right", valign: "middle" },   // Total Children
            11: { cellWidth: 11, halign: "right", valign: "middle" },   // Total Elderly
            12: { cellWidth: 11, halign: "right", valign: "middle" },   // Household
        } : {
            0: { cellWidth: 10, halign: "center", valign: "middle" },   // Sl No
            1: { cellWidth: 25, halign: "left", valign: "middle" },     // Municipality
            2: { cellWidth: 25, halign: "left", valign: "middle" },     // Post Admin
            3: { cellWidth: 25, halign: "center", valign: "middle" },   // Date/Time
            4: { cellWidth: 18, halign: "center", valign: "middle" },   // Advisory
            5: { cellWidth: 16, halign: "right", valign: "middle" },    // Total Population
            6: { cellWidth: 14, halign: "right", valign: "middle" },    // Total Male
            7: { cellWidth: 14, halign: "right", valign: "middle" },    // Total Female
            8: { cellWidth: 16, halign: "right", valign: "middle" },    // Total Children
            9: { cellWidth: 14, halign: "right", valign: "middle" },    // Total Elderly
            10: { cellWidth: 14, halign: "right", valign: "middle" },   // Household
        };

        autoTable(pdf, {
            startY: currentY,
            head: lightningTableHead,
            body: tableDataForPdf,
            theme: "grid",
            styles: {
                fontSize: 7,
                cellPadding: 2,
                lineWidth: 0.1,
                lineColor: [150, 150, 150],
                overflow: 'linebreak'
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: [255, 255, 255],
                fontSize: 7.5,
                halign: "center",
                valign: "middle"
            },
            margin: { left: sideMargin, right: sideMargin, top: headerHeight },
            columnStyles: columnStylesTable,
            didDrawPage: (data) => {
                addBannerToPage(pdf);
                currentY = data.cursor.y + 10;
            },
        });
        currentY = pdf.lastAutoTable.finalY + 5;

        // ===== GENERAL ADVISORY SECTION =====
        const advisoryTableData = t.advisoryPoints.map(item => {
            const points = item.points.map(p => `• ${p}`);
            return [item.type, points.join("\n")];
        });

        autoTable(pdf, {
            startY: currentY,
            head: [t.advisoryTableHeaders],
            body: advisoryTableData,
            theme: "grid",
            headStyles: {
                fillColor: [225, 232, 242],
                halign: "left",
                fontStyle: "bold",
                textColor: "black",
            },
            styles: {
                fontSize: 9,
                cellPadding: { top: 3, right: 3, bottom: 3, left: 5 },
                overflow: "linebreak",
                lineHeight: 1.2,
            },
            columnStyles: {
                0: { cellWidth: 'auto', halign: "left" },
                1: { cellWidth: pageWidth - 2 * sideMargin - 60, halign: "left" },
            },
            margin: { left: sideMargin, right: sideMargin, top: headerHeight },
            didDrawPage: (data) => {
                addBannerToPage(pdf);
                currentY = data.cursor.y + 10;
            },
        });
        currentY = pdf.lastAutoTable.finalY + 5;

        // ===== NOTE SECTION =====
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        const noteTextLines = pdf.splitTextToSize(t.noteText.trim().replace(/\s+/g, ' '), pageWidth - 2 * sideMargin);
        const estimatedNoteHeight = noteTextLines.length * 6 + 12;

        if (currentY + estimatedNoteHeight > availablePageHeight + headerHeight) {
            currentY = addPageWithHeader();
        }

        pdf.setFont("helvetica", "bold");
        pdf.text(t.noteLabel, sideMargin, currentY);
        currentY += 6;
        pdf.setFont("helvetica", "normal");
        noteTextLines.forEach((line) => {
            pdf.text(line, sideMargin, currentY);
            currentY += 6;
        });
        pdf.setFont("helvetica", "bold");
        pdf.text("*****", (pageWidth - pdf.getTextWidth("*****")) / 2, currentY);

        // ===== FOOTER =====
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.text(t.footer, pageWidth / 2, pageHeight - 7, { align: "center" });
            pdf.text(t.page.replace("[i]", i).replace("[pageCount]", pageCount), pageWidth / 2, pageHeight - 3, { align: "center" });
        }

        pdf.save(`DRRDSS-Lightning-${moduleName}-Advisory.pdf`);
    } catch (error) {
        console.error("Error generating PDF:", error);
        alert("Failed to generate PDF. Please try again.");
    } finally {
        setIsGeneratingReport(false);
    }
};
